import { Model } from 'app/model/backendmodel';
import { AzureTableSink } from '../../../pipeline/copysink/azure-table.copysink';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { TabularCopySink } from 'app/model/pipeline/copysink/tabular.copysink';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureTableSink),
    asynchronous: true,
    adapts: {
        type: '*',
        writeBatchTimeout: '*',
        writeBatchSize: '*',
        azureTablePartitionKeyName: '*',
        azureTableDefaultPartitionKeyValue: '*',
        azureTableRowKeyName: '*',
        azureTableInsertType: '*',
        maxConcurrentConnections: '*',
        structure: '*'
    }
})
export class AzureTableSinkAdapter extends CopySinkAdapter<AzureTableSink, Model.AzureTableSink> {
    protected override async _toResource(model: AzureTableSink): Promise<Model.AzureTableSink> {
        const result: Model.AzureTableSink = {
            type: 'AzureTableSink'
        };
        result.azureTableInsertType = ValueAdapter.toResource(model, AzureTableSink.INSER_TYPE);
        if (model.useColumnNameForPartitionKey) {
            result.azureTablePartitionKeyName = ValueAdapter.toResource(model, AzureTableSink.PARTITION_KEY_NAME);
        } else {
            const defaultPartitionKeyValue = ValueAdapter.toResource(model, AzureTableSink.DEFAULT_PARTITION_KEY_VALUE);
            if (defaultPartitionKeyValue) {
                result.azureTableDefaultPartitionKeyValue = defaultPartitionKeyValue;
            }
        }
        if (model.useColumnNameForRowKey) {
            result.azureTableRowKeyName = ValueAdapter.toResource(model, AzureTableSink.ROW_KEY_NAME);
        }
        result.writeBatchSize = ValueAdapter.toResource(
            model,
            Metamodel.modelFor(AzureTableSink).attribute('writeBatchSize')
        );
        const writeBatchTimeout = ValueAdapter.toResource(model, TabularCopySink.WRITE_BATCH_TIMEOUT);
        if (writeBatchTimeout != null && writeBatchTimeout !== '') {
            // don't need pass '' to backend
            result.writeBatchTimeout = writeBatchTimeout;
        }
        CopySinkAdapter.maxConcurrentConnectionsToResource(model, result);
        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected override async _toModel(resource: Model.AzureTableSink, owner: Activity): Promise<AzureTableSink> {
        const result = Owned.create<AzureTableSink>(Metamodel.modelFor(AzureTableSink), owner);
        if (resource.azureTableInsertType) {
            ValueAdapter.toModel(result, AzureTableSink.INSER_TYPE, resource.azureTableInsertType);
        }
        if (resource.azureTablePartitionKeyName) {
            ValueAdapter.toModel(result, AzureTableSink.PARTITION_KEY_NAME, resource.azureTablePartitionKeyName);
            result.useColumnNameForPartitionKey = true;
        } else if (resource.azureTableDefaultPartitionKeyValue) {
            ValueAdapter.toModel(
                result,
                AzureTableSink.DEFAULT_PARTITION_KEY_VALUE,
                resource.azureTableDefaultPartitionKeyValue
            );
            result.useColumnNameForPartitionKey = false;
        } else {
            result.useColumnNameForPartitionKey = false;
        }
        if (resource.azureTableRowKeyName) {
            ValueAdapter.toModel(result, AzureTableSink.ROW_KEY_NAME, resource.azureTableRowKeyName);
            result.useColumnNameForRowKey = true;
        } else {
            result.useColumnNameForRowKey = false;
        }
        ValueAdapter.toModel(
            result,
            Metamodel.modelFor(AzureTableSink).attribute('writeBatchSize'),
            resource.writeBatchSize
        );
        ValueAdapter.toModel(result, TabularCopySink.WRITE_BATCH_TIMEOUT, resource.writeBatchTimeout);
        CopySinkAdapter.maxConcurrentConnectionsToModel(resource, result);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }
}
